import { useState } from "react";
import { Box } from "@mui/material";
import {
  Button,
  Checkbox,
  Icon,
  TextField,
  Typography,
} from "@platform/design-system";

import type { Broker } from "../types";

interface BrokerChecklistProps {
  brokers: Broker[];
  searchInputId: string;
}

const matchesSearch = (broker: Broker, search: string): boolean =>
  broker.name.toLowerCase().includes(search.trim().toLowerCase());

export const BrokerChecklist = ({
  brokers,
  searchInputId,
}: BrokerChecklistProps): React.JSX.Element => {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const toggleBroker = (id: number, checked: boolean): void =>
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id),
    );

  return (
    <Box
      data-testid="broker-checklist"
      display="flex"
      flexDirection="column"
      gap={1}
    >
      <TextField
        id={searchInputId}
        fullWidth
        placeholder="Search agency or office (from Salesforce)"
        startAdornment={<Icon name="search" size="sm" color="muted" />}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <Box
        border={1}
        borderColor="divider"
        borderRadius={1}
        maxHeight={240}
        overflow="auto"
        py={0.5}
      >
        {brokers
          .filter((broker) => matchesSearch(broker, search))
          .map(({ id, name }) => (
            <Box
              key={id}
              data-testid={`broker-checklist-item-${id}`}
              component="label"
              display="flex"
              alignItems="center"
            >
              <Checkbox
                checked={selectedIds.includes(id)}
                onChange={(_, checked) => toggleBroker(id, checked)}
              />
              <Typography variant="body-medium" component="span">
                {name}
              </Typography>
            </Box>
          ))}
      </Box>
      <Box>
        <Button
          variant="secondary"
          size="sm"
          disabled={selectedIds.length === 0}
        >
          Add selected brokers
        </Button>
      </Box>
    </Box>
  );
};
